/**
 * French catalogue — the source of truth for the key set.
 *
 * The previous UI carried 31 `data-i18n` attributes over ~9400 lines of
 * hardcoded French, so the FR/EN switch translated almost nothing and the
 * English build was unusable. Here the English catalogue is typed against
 * this object, which makes a missing translation a compile error rather than
 * a string that silently stays French in front of a customer.
 *
 * Accents are deliberate: the old markup had lost them in a large share of
 * its strings ("Televerser", "Aucun dataset selectionne", "Activite"), which
 * reads as unfinished in a French-language demo.
 */
export const fr = {
  common: {
    team: "Équipe",
    region: "Région",
    prefix: "Préfixe",
    accessKey: "Clé d’accès",
    secretKey: "Clé secrète",
    host: "Hôte",
    port: "Port",
    endpoint: "Adresse",
    expiresAt: "Expiration",
    expired: "Expiré",
    revoke: "Révoquer",
    createToken: "Créer un jeton",
    neverUsed: "jamais utilisé",
    actions: "Actions",
    add: "Ajouter",
    cancel: "Annuler",
    close: "Fermer",
    copy: "Copier",
    create: "Créer",
    delete: "Supprimer",
    edit: "Modifier",
    error: "Erreur",
    export: "Exporter",
    loading: "Chargement…",
    name: "Nom",
    description: "Description",
    open: "Ouvrir",
    owner: "Propriétaire",
    reader: "Lecteur",
    writer: "Rédacteur",
    save: "Enregistrer",
    search: "Rechercher",
    settings: "Paramètres",
    status: "État",
    createdAt: "Créé le",
    updatedAt: "Mis à jour",
    startedAt: "Démarré",
    duration: "Durée",
    project: "Projet",
    type: "Type",
    size: "Taille",
    role: "Rôle",
    user: "Utilisateur",
    organization: "Organisation",
    none: "Aucun",
    yes: "Oui",
    no: "Non",
    required: "Ce champ est obligatoire.",
    accessDenied: "Vous n’avez pas les droits nécessaires pour cette action.",
    viewLogs: "Voir les journaux",
    logs: "Journaux",
  },

  nav: {
    componentCredentials: "Identifiants de composant",
    serviceAccounts: "Comptes de service",
    administrator: "Admin",
    home: "Accueil",
    projects: "Projets",
    catalog: "Catalogue",
    agents: "Agents",
    production: "Production",
    administration: "Administration",
    overview: "Vue d’ensemble",
    workspaces: "Workspaces",
    jobs: "Jobs",
    apps: "Applications",
    apis: "API",
    dashboards: "Dashboards",
    data: "Données",
    environments: "Environnements",
    members: "Membres",
    settings: "Paramètres",
    datasets: "Datasets",
    datasources: "Sources de données",
    ontologies: "Ontologies",
    repositories: "Dépôts Git",
    network: "Réseau et sécurité",
    rbac: "Accès",
    activity: "Activité",
    audit: "Audit",
    backups: "Sauvegardes",
    identity: "Utilisateurs et organisations",
    dataGovernance: "Gouvernance des données",
    inventory: "Logiciels et licences",
    storage: "Stockage",
    backToProjects: "Tous les projets",
    documentation: "Documentation",
    apiReference: "Référence API",
    toggleSidebar: "Réduire le menu",
    theme: "Thème",
    themeLight: "Clair",
    themeDark: "Sombre",
    themeSystem: "Système",
    language: "Langue",
    signIn: "Se connecter",
    signOut: "Se déconnecter",
    skipToContent: "Aller au contenu principal",
  },

  storageCapacity: {
    title: "Place restante pour les volumes",
    intro:
      "Ce que le cluster peut encore accepter. Une demande de volume est acceptée à vue et n’échoue qu’au moment de l’attachement : sans cet écran, la première alerte est un utilisateur qui ne peut pas travailler.",
    claimedOf: "réservés sur {total} allouables",
    freeDisk: "{free} de disque réellement inutilisé",
    leftToAllocate: "{left} encore allouables",
    nodeDetail: "{claimed} réservés sur {allocatable} · {free} de disque libre",
    tight: "Bientôt plein",
    full: "Plein",
    unavailable:
      "La couche de stockage de cette installation ne sait pas répondre. La plateforme ne peut donc pas prévenir à l’avance — elle préfère le dire plutôt que d’afficher une jauge inventée.",
    explain:
      "Un volume est placé en fonction de ce qu’il réserve, pas du disque libre. C’est pourquoi les deux chiffres diffèrent, et pourquoi un cluster presque vide peut refuser un volume de plus.",
  },
  platformRoles: {
    title: "Ce qu'un rôle peut créer",
    intro:
      "Les fonctions en ligne, les rôles en colonne. Trois rôles sont fournis : l'administrateur agit sur ce qui n'est pas à lui, l'utilisateur sur le sien, le consultant consulte. Leur nom reste celui de la plateforme, leurs cases sont à vous — sauf celles de l'administrateur, la ligne sur laquelle l'installation retombe. Ajoutez vos propres rôles à côté. Ce que chacun peut lire reste un octroi, sur l'objet lui-même.",
    function: "Fonction",
    heldBy: "porté par {count}",
    changed: "Modifié ici",
    shipped: "Fourni",
    notAssignable: "Rôle de l'annuaire",
    addRole: "Ajouter un rôle",
    keyPlaceholder: "data-steward",
    namePlaceholder: "Data steward",
    descriptionPlaceholder: "Ce que ce rôle fait, en une phrase",
    save: "Enregistrer",
    saved: "Rôle enregistré",
    remove: "Supprimer",
    removed: "Rôle supprimé",
    role: "Rôle",
    adminHint: "Administrateur de la plateforme : un rôle de l'annuaire, qui agit sur ce qui n'est pas à lui. Il se nomme et se retire ici par un autre administrateur ; jamais soi-même, jamais le dernier.",
    signInAgain: "La personne doit se reconnecter pour que son rôle prenne effet.",
    withdrawAdmin: "Retirer le rôle administrateur",
    roleHint: "Ce que cette personne peut créer. Sans attribution, c'est le rôle utilisateur.",
    assign: "Attribuer",
    assigned: "Rôle attribué",
    reset: "Remettre utilisateur",
    defaultRole: "par défaut",
    "create-project": "Créer un projet",
    "launch-workspace": "Lancer un workspace",
    "launch-job": "Lancer un job",
    "deploy-app": "Déployer une application",
    "build-environment": "Construire un environnement",
    "create-dataset": "Créer un dataset",
    "scan-ontology": "Scanner une ontologie",
    "create-extract": "Créer un extrait",
    "declare-datasource": "Déclarer une source de données",
    "declare-repository": "Déclarer un dépôt",
    "register-regulated-dataset": "Enregistrer un dataset réglementé",
    "attach-regulated-data": "Rattacher une donnée réglementée à un projet",
    "administer-organization": "Administrer une organisation",
  },
  roleMatrix: {
    title: "Rôles de projet",
    intro:
      "Ce qu’un rôle donné dans un projet permet : lire ou modifier le projet et ses membres, rattacher des données, lancer des charges, gérer les environnements. Jamais lire ni écrire la donnée elle-même : c’est un octroi sur l’objet, et rattacher ne le donne pas. Trois rôles fournis et verrouillés ; vos propres rôles s’écrivent ici et se donnent sur l’écran des membres.",
    addRole: "Ajouter un rôle",
    basedOn: "Hérite de",
    namePlaceholder: "Data steward",
    descriptionPlaceholder: "Ce que ce rôle fait, en une phrase",
    shipped: "Livré",
    shippedBase: "Rôle de la plateforme",
    columnProject: "Projet",
    columnDataset: "Rattacher des datasets",
    columnOntology: "Rattacher des ontologies et extraits",
    columnDatasource: "Rattacher des sources",
    columnEnvironment: "Gérer les environnements",
    columnWorkload: "Lancer des charges",
    yes: "oui",
    no: "non",
    baseViewer: "Lecteur",
    baseEditor: "Contributeur",
    baseAdmin: "Administrateur",
    heldBy: "porté par {count}",
    saved: "Rôles enregistrés",
    ceilingHint:
      "Un rôle maison hérite toujours d’un rôle de la plateforme : c’est ce que la personne obtient partout où la matrice n’a rien à dire, et c’est aussi son plafond tant que la matrice n’applique pas la ligne.",
  },
  agentGovernance: {
    title: "Agents",
    intro:
      "Ce que les agents de cette installation ont le droit de faire, et ce qu’ils ont fait. L’activité est comptée sur les actions relevées par la plateforme, jamais sur ce que leurs rapports affirment.",
    agents: "Agents",
    canAct: "Peuvent agir",
    canActHint: "Les autres regardent et rapportent",
    mandates: "Demandes autorisées",
    actions: "Actions effectuées",
    window: "Sur {days} jours",
    mayDo: "Droit d’agir",
    state: "État",
    activity: "Activité",
    runsAndActions: "{runs} passages · {actions} actions",
    lastRun: "Dernier passage",
    noAgents: "Aucun agent sur cette installation",
    noAgentsHint:
      "Les agents se recrutent dans un projet, et n’apparaissent ici qu’une fois créés.",
    organisationIntro:
      "Écrite d’avance : un responsable ne peut demander à un membre d’agir que si la ligne existe ici. Une ligne enregistrée est une ligne qui fonctionne.",
    noMandates: "Personne ne peut rien demander à personne.",
    grantedBy: "Autorisé par {who}, {when}",
  },
  agents: {
    tabAgents: "Agents",
    tabWorkflows: "Workflows",
    tabTeams: "Équipes",
    statAgents: "Agents",
    statAgentsHint: "{active} au travail sur {total}",
    statWorkflows: "Workflows",
    statWorkflowsHint: "{active} en service sur {total}",
    statDecisions: "À décider",
    statDecisionsHint: "Des runs attendent une personne",
    statDecisionsNone: "Rien n’attend personne",
    statGrants: "Droits d’agir",
    statGrantsHint: "{granted} peuvent agir, {readers} regardent",
    inboxTitle: "À décider",
    inboxIntro:
      "Ces runs attendent une personne nommée. Ils reprennent dès que vous avez tranché, et ne font rien d’ici là.",
    inboxStep: "pas {index} · {step}",
    grants: "Droits d’agir",
    conceptLead:
      "Un agent est ce que vous voyez : un collègue à qui vous laissez une consigne. Un workflow est ce qui s’exécute : une suite de pas, chacun confié au modèle ou à une personne. Un agent d’aujourd’hui est un workflow d’un seul pas.",
    title: "Agents",
    intro:
      "Des consignes permanentes, écrites dans vos mots. Un agent regarde à votre place et vous dit ce qu’il a vu.",
    recruit: "Recruter",
    recruitFirst: "Recruter mon premier agent",
    recruitTitle: "Recruter un agent",
    editTitle: "Modifier l’agent",
    recruitIntro:
      "Donnez-lui un nom et dites-lui ce que vous attendez, comme à quelqu’un qui arrive.",
    nameLabel: "Son nom",
    namePlaceholder: "Clémence",
    missionLabel: "Son travail",
    missionHelp:
      "Écrivez-le en français courant. Rien ici n’est un mot-clé : la consigne part telle quelle.",
    missionPlaceholder:
      "Tu surveilles mes workspaces. Quand l’un ne démarre pas, tu me dis lequel et pourquoi, en une phrase.",
    examplesTitle: "Par exemple :",
    exampleWorkspaces:
      "Tu surveilles mes workspaces. Quand l’un ne démarre pas, tu me dis lequel et pourquoi, en une phrase.",
    exampleApps:
      "Tu vérifies mes applications. Si l’une est arrêtée, tu la relances et tu me le dis.",
    exampleQuiet:
      "Une fois par jour, tu fais le tour de mes projets et tu me signales ce qui a changé.",
    scheduleLabel: "Ses horaires",
    scheduleManual: "À la demande",
    scheduleHourly: "Toutes les heures",
    scheduleDaily: "Une fois par jour",
    mayRestartLabel: "Il peut relancer une application arrêtée",
    mayRestartHelp:
      "Sans cela il regarde et rapporte, sans rien changer. C’est la seule chose qu’un agent peut modifier, et chaque relance est tracée.",
    mayRestart: "Peut relancer une application",
    mayCallApiLabel: "Il peut agir sur la plateforme par son API",
    mayCallApiHelp:
      "Lancer un job, écrire dans un dataset, déclarer un extrait, arrêter une application : tout ce que vous pouvez faire vous-même, en votre nom et avec vos droits. Chaque appel est tracé.",
    mayCallApi: "Peut agir par l’API",
    readsOnly: "Regarde et rapporte",
    hire: "Recruter",
    hired: "{name} est en poste.",
    updated: "Modifié.",
    teamsTitle: "Équipes",
    teamsIntro:
      "Plusieurs agents sur un même sujet. Vous écrivez d’avance qui peut demander quoi à qui, et rien d’autre ne se produit.",
    teamsEmpty:
      "Vos agents travaillent chacun de leur côté. Une équipe leur donne un sujet commun et vous laisse écrire ce qu’ils peuvent se demander.",
    formTeam: "Former une équipe",
    formFirstTeam: "Former ma première équipe",
    formTeamTitle: "Former une équipe",
    formTeamIntro: "Donnez-lui un nom et dites sur quoi elle travaille.",
    teamNameLabel: "Son nom",
    teamNamePlaceholder: "Astreinte",
    teamPurposeLabel: "Son sujet",
    teamPurposePlaceholder:
      "Garder les applications de production en marche pendant la nuit.",
    teamFormed: "L’équipe {name} est formée.",
    teamMembers: "Qui en fait partie",
    teamNoMembers:
      "Personne pour l’instant. Placez un agent dans cette équipe depuis sa fiche.",
    organisation: "Qui peut demander quoi",
    organisationHelp:
      "Une ligne enregistrée est une ligne qui fonctionne : si la demande ne pourrait pas aboutir, elle est refusée tout de suite avec sa raison.",
    organisationEmpty:
      "Personne ne peut encore rien demander à personne. Un responsable peut demander à un membre d’agir, une fois que vous l’avez écrit ici.",
    mandateSentence: "{lead} peut demander à {member} de {action}.",
    askActionRestartApp: "relancer une application arrêtée",
    askActionCallApi: "agir sur la plateforme par son API",
    allowAsk: "Autoriser une demande",
    allowAskTitle: "Autoriser une demande",
    allowAskIntro:
      "Choisissez qui demande et à qui. Cela n’accorde aucun droit nouveau : cela dit seulement qui a le droit de le demander.",
    askLeadLabel: "Qui demande",
    askMemberLabel: "À qui",
    askActionLabel: "De faire quoi",
    askWritten: "C’est écrit.",
    withdraw: "Retirer",
    withdrawn: "Retiré.",
    needsALead:
      "Il faut au moins un responsable dans l’équipe. Un agent devient responsable depuis sa fiche.",
    roleObserver: "Observe et rapporte",
    roleOperator: "Peut agir",
    roleLead: "Responsable",
    roleLabel: "Son rôle dans l’équipe",
    teamLabel: "Son équipe",
    noTeam: "Travaille seul",
    dismiss: "Renvoyer",
    orphansTitle: "Agents sans projet",
    orphansHint:
      "Ils n'apparaissent sur aucune page de projet et ne s'exécutent plus. Renvoyez-les, ou recréez-les dans un projet.",
    dismissTitle: "Renvoyer {name} ?",
    dismissBody:
      "Sa consigne et son carnet de travail sont supprimés. Ce qu’il a relancé ou signalé reste dans l’audit de la plateforme.",
    dismissed: "{name} a été renvoyé.",
    ask: "Demander",
    askPlaceholder: "Demandez quelque chose à {name}…",
    youAsked: "Vous :",
    runNow: "Faire maintenant",
    atWork: "Au travail",
    paused: "En pause",
    never: "Jamais travaillé",
    justNow: "À l’instant",
    minutesAgo: "Il y a {count} min",
    hoursAgo: "Il y a {count} h",
    daysAgo: "Il y a {count} j",
    whatTheySaw: "Ce que {name} a vu",
    nothingToReport: "Rien à signaler",
    reported: "Il a quelque chose à dire.",
    failed: "En échec",
    loadingRuns: "Lecture du carnet…",
    neverRan:
      "Il n’a pas encore travaillé. Lancez-le maintenant, ou attendez ses horaires.",
    emptyLead:
      "Vous n’avez encore recruté personne. Un agent est quelqu’un à qui vous laissez une consigne permanente, écrite comme vous la diriez :",
    actionRestartApp: "Application relancée",
    actionCallApi: "Appel API {call}",
  },
  workflows: {
    pipelineTitle: "La séquence",
    triggerNode: "Déclencheur",
    reportNode: "Rapport",
    whenTitle: "Quand",
    whenInWords:
      "Dire quand, en mots : « tous les matins à 8 h sauf le week-end »",
    soon: "Bientôt",
    onEvent: "Sur un événement",
    eventJobFinished: "À la fin d’un job",
    eventDatasetChanged: "Quand un dataset change",
    stepVerbs: "Verbes",
    stepReadsOnly: "Regarde et rapporte",
    edit: "Modifier",
    currentInstruction: "La consigne du pas en cours",
    title: "Workflows",
    intro:
      "Ce qui s’exécute quand un agent travaille : une suite de pas, chacun confié au modèle ou à une personne nommée. Le déroulé décide de la suite, pas le modèle.",
    create: "Écrire un workflow",
    createFirst: "Écrire le premier",
    emptyLead:
      "Un workflow enchaîne des pas. Un agent cherche, une personne relit, un second agent analyse ce que le premier a trouvé — et chaque pas est écrit avant que le suivant commence.",
    editTitle: "Un workflow",
    createIntro:
      "Un nom, des horaires, et des pas dans l’ordre où ils se suivent.",
    nameLabel: "Nom",
    namePlaceholder: "Veille puis analyse",
    scheduleLabel: "Horaires",
    stepsLabel: "Les pas, dans l’ordre",
    stepsHelp:
      "Un pas agent reçoit sa consigne et ce que le pas précédent a produit. Un pas d’approbation attend une personne et ne consomme rien.",
    addAgentStep: "Ajouter un pas agent",
    addApprovalStep: "Ajouter une approbation",
    stepAgent: "Agent",
    stepApproval: "Approbation",
    stepNameLabel: "Intitulé",
    stepNamePlaceholder: "Chercher",
    stepInstructionLabel: "Consigne",
    stepInstructionPlaceholder:
      "Cherche ce qui est nouveau sur le sujet et dis-le en trois phrases.",
    stepApproverLabel: "Qui approuve",
    stepApproverHelp:
      "L’identifiant de la personne, membre du projet. Elle seule pourra laisser passer ou arrêter.",
    stepMayRestart: "Peut relancer une application",
    stepMayCallApi: "Peut agir par l’API",
    removeStep: "Retirer ce pas",
    moveUp: "Monter",
    moveDown: "Descendre",
    save: "Écrire",
    written: "{name} est écrit.",
    deleted: "{name} est supprimé, avec ses runs.",
    delete: "Supprimer",
    deleteTitle: "Supprimer {name} ?",
    deleteBody:
      "Le workflow et tous ses runs disparaissent. Ce que ses pas ont fait reste dans l’audit de la plateforme.",
    runNow: "Lancer maintenant",
    started: "Lancé.",
    missionTitle: "Ce pourquoi il est là",
    whereItIs: "Où il en est",
    idle: "Rien en cours",
    atStep: "Au pas {index} — {name}",
    lastRun: "Dernier run",
    nextRun: "Prochain run",
    nextManual: "Quand quelqu’un le lance",
    nextHourly: "Dans l’heure",
    nextDaily: "Dans la journée",
    runsTitle: "Les runs",
    loadingRuns: "Lecture des runs…",
    neverRan: "Il n’a pas encore tourné.",
    approve: "Laisser passer",
    reject: "Arrêter",
    approved: "Le run continue.",
    rejected: "Le run est arrêté.",
    waitingFor: "Attend {name}",
    attempts: "{count} tentative(s)",
    statusPending: "En attente",
    statusRunning: "En cours",
    statusWaiting: "Attend une personne",
    statusSucceeded: "Terminé",
    statusFailed: "En échec",
    statusCancelled: "Arrêté",
    stepUntitled: "Pas sans nom",
    editHint:
      "Cliquez + sur une flèche pour insérer un pas, ou un pas pour le modifier.",
    insertHere: "Insérer un pas ici",
    moveLeft: "Avancer",
    moveRight: "Reculer",
    updated: "{name} est mis à jour.",
    triggerTitle: "Le déclencheur",
    triggerHelp: "Quand le déroulé démarre. Les pas suivent, dans l’ordre.",
    editing: "Modification",
    stepKindLabel: "Sorte de pas",
    canvasTitle: "Le déroulé",
    inspectorTitle: "Le pas",
    selectStep: "Cliquez un pas pour le lire.",
    stepStatusTitle: "Où il en est",
    stepInstructionTitle: "Sa consigne",
    stepOutput: "Ce qu’il a produit",
    stepActions: "Ce que la plateforme l’a vu faire",
    stepError: "Pourquoi il a échoué",
    stepNoRun: "Pas encore exécuté.",
  },
  aiServices: {
    title: "Services d’IA",
    full: "Service complet",
    degraded: "Mode réduit",
    down: "Arrêté",
  },
  home: {
    title: "Bonjour {name}",
    subtitle: "Reprenez votre travail ou démarrez un nouveau projet.",
    recentProjects: "Projets récents",
    recentProjectsHint: "Vos projets les plus récemment actifs.",
    quickActions: "Démarrer",
    newProject: "Nouveau projet",
    newProjectHint: "Créer un espace de travail collaboratif",
    browseCatalog: "Parcourir le catalogue",
    browseCatalogHint: "Datasets, sources de données et ontologies",
    platformActivity: "Activité de la plateforme",
    users: "Utilisateurs",
    usersHint: "Comptes sur la plateforme",
    projects: "Projets",
    projectsHint: "Espaces collaboratifs",
    workloads: "Exécutions",
    workloadsHint: "Workspaces, jobs et applications actifs",
    storage: "Stockage",
    storagePartial:
      "Sur {measured} datasets. {pending} pas encore mesurés : le volume réel est plus élevé.",
    storagePending: "Pas encore mesuré — la première passe tourne cette nuit.",
    storageAsOf: "Sur les {count} datasets, mesuré {when}.",
    storageUnreadable:
      "Sur {measured} datasets. {unreadable} n'a pas pu être lu : à réparer, ce total est incomplet.",
    storageHint: "Déclarés sur la plateforme",
    noProjects: "Aucun projet pour le moment",
    noProjectsHint:
      "Un projet regroupe vos workspaces, vos exécutions, vos données et les personnes qui y travaillent.",
  },

  projects: {
    deleteHoldsWarning:
      "Ce projet porte encore {count} élément(s), dont {running} en marche. Les supprimer est irréversible.",
    deleteHoldsConfirm:
      "Vous allez détruire le projet et les {count} élément(s) listés, dont {running} en marche. Tapez le nom du projet pour confirmer.",
    storageSizeLabel: "Capacité des workspaces",
    storageSizeHint:
      "Volume monté sur /mnt dans chaque workspace du projet, conservé entre les redémarrages. Les workspaces déjà lancés gardent leur volume.",
    storageSizeDefault: "Valeur par défaut de la plateforme",
    title: "Projets",
    subtitle:
      "Un projet regroupe vos workspaces, exécutions, données et collaborateurs.",
    create: "Nouveau projet",
    createTitle: "Créer un projet",
    createHint:
      "Un nom et une description suffisent pour démarrer. Tout le reste se configure ensuite.",
    nameLabel: "Nom du projet",
    nameHint:
      "Visible par tous les membres. Vous pourrez le changer plus tard.",
    namePlaceholder: "Analyse des ventes 2026",
    descriptionHint:
      "Une phrase sur l’objectif du projet, pour vos collaborateurs.",
    search: "Rechercher un projet",
    empty: "Aucun projet",
    emptyHint: "Créez votre premier projet pour commencer à travailler.",
    lastActivity: "Dernière activité",
    resources: "Ressources actives",
    deleteTitle: "Supprimer le projet",
    deleteWarning:
      "Cette action est irréversible. Les workspaces, jobs et applications du projet seront arrêtés et supprimés.",
    transferOwnership: "Transférer la propriété",
    ownedByUser: "Appartient à une personne",
    ownedByOrganization: "Appartient à une organisation",
    ownerCurrent: "Propriétaire actuel :",
    ownerUnchangedHint: "C’est le propriétaire actuel. Choisissez quelqu’un d’autre pour transférer.",
    ownerTypeLabel: "Type de propriétaire",
    ownerLabel: "Nouveau propriétaire",
    viewMode: "Affichage",
    viewGrid: "Tuiles",
    viewList: "Liste",
  },

  projectOverview: {
    title: "Vue d’ensemble",
    subtitle: "État courant du projet et raccourcis vers ses ressources.",
    activeWorkspaces: "Workspaces actifs",
    runningJobs: "Jobs en cours",
    runningApps: "Applications actives",
    attachedDatasets: "Datasets rattachés",
    quickLaunch: "Démarrer un workspace",
    recentActivity: "Activité récente",
    noActivity: "Aucune activité récente sur ce projet.",
    settingsTitle: "Paramètres du projet",
    settingsHint: "Nom, description et propriétaire du projet.",
  },

  workspaces: {
    environmentUnknown: "Environnement retiré",
    rebuiltSince:
      "L’environnement a été reconstruit depuis le lancement — relancez pour obtenir la nouvelle image.",
    rebuiltSinceShort: "reconstruit depuis",
    assistantSendsDataOffSite:
      "Ce projet monte des données de santé ({datasets}). L'assistant lit les fichiers du workspace pour vous répondre, et ce qu'il lit est envoyé au modèle, hébergé hors du site.",
    startupTitle: "Démarrage en cours",
    startupHint:
      "Les grandes étapes du lancement. Cet écran disparaît dès que l’espace de travail est ouvert.",
    startupStuck: "Le démarrage est arrêté : il ne repartira pas tout seul.",
    startupTechnical: "Détail technique",
    step_requested: "Demande enregistrée",
    step_scheduled: "Machine réservée",
    step_image: "Environnement récupéré",
    step_storage: "Données rattachées",
    step_environment: "Préparation de la session",
    reason_no_capacity:
      "Aucune machine ne peut accueillir cette taille pour le moment.",
    reason_image_unavailable:
      "L’image de l’environnement n’a pas pu être récupérée.",
    reason_storage_unavailable:
      "Le stockage des données n’a pas pu être rattaché. Prévenez votre administrateur.",
    reason_environment_failed:
      "La session s’est arrêtée pendant sa préparation.",
    reason_environment_out_of_disk:
      "La session a écrit plus que son espace temporaire autorisé. Installez les environnements et les caches dans /mnt, ~/.noryx-profile ou /datasets — seuls ces emplacements survivent à un arrêt.",
    reason_environment_restarting: "La session redémarre en boucle.",
    reason_environment_out_of_memory:
      "Fermée pour cause de manque de mémoire. Relancez sur une taille de machine plus grande.",
    title: "Workspaces",
    subtitle: "Vos environnements de travail interactifs sur ce projet.",
    create: "Nouveau workspace",
    createTitle: "Lancer un workspace",
    createHint:
      "Choisissez un environnement et une taille de machine. Le workspace démarre en quelques secondes.",
    environmentLabel: "Environnement",
    environmentHint:
      "Détermine les bibliothèques disponibles et l’IDE utilisé.",
    tierLabel: "Taille de machine",
    tierHint:
      "Vous pourrez relancer le workspace sur une taille différente à tout moment.",
    nameLabel: "Nom du workspace",
    nameHint: "Pour vous y retrouver si vous en lancez plusieurs.",
    namePlaceholder: "Exploration des ventes",
    storageLabel: "Espace de travail persistant",
    empty: "Aucun workspace",
    emptyHint:
      "Un workspace est un environnement de développement prêt à l’emploi : notebook, éditeur et données du projet déjà montées.",
    openPending: "Démarrage en cours…",
    stopTitle: "Arrêter le workspace",
    stopWarning:
      "Le contenu de /mnt est conservé. Les processus en cours et le contenu hors /mnt seront perdus.",
    stop: "Arrêter",
    launched: "Workspace lancé. Il sera accessible dès qu’il aura démarré.",
    restart: "Redémarrer",
    launchedBy: "Lancé par",
    foreignHint:
      "Cette session a été lancée par quelqu'un d'autre : elle monte ses droits sur les données, pas les vôtres. Lancez la vôtre.",
    egressClosed: "Sans accès direct à Internet : la donnée réglementée de ce projet ferme la sortie réseau. Les paquets et les dépôts passent par les miroirs internes.",
    withheld: "Non monté, faute de droit sur la donnée :",
    restartTitle: "Redémarrer le workspace",
    restartWarning: "Le logiciel redémarre ; vos fichiers et vos données montées ne bougent pas. Utile quand une extension ne se charge qu'au démarrage. Comptez une minute avant de pouvoir rouvrir.",
    restarted: "Le workspace redémarre.",
    stopped: "Workspace arrêté.",
    notReady: "Le workspace n’est pas encore prêt.",
    resources: "Ressources",
  },

  jobs: {
    title: "Jobs",
    subtitle: "Exécutions ponctuelles et planifiées de ce projet.",
    create: "Nouveau job",
    createTitle: "Lancer un job",
    createHint:
      "Un job exécute une commande dans un environnement, une fois ou selon une planification.",
    nameLabel: "Nom du job",
    namePlaceholder: "Rafraîchissement quotidien",
    commandLabel: "Commande à exécuter",
    commandHint:
      "Exécutée à la racine du projet, avec /mnt et les datasets déjà montés.",
    commandPlaceholder: "python scripts/refresh.py",
    modeLabel: "Déclenchement",
    modeOnce: "Une seule fois, maintenant",
    modeSchedule: "Selon une planification",
    scheduleLabel: "Planification",
    scheduleHint:
      "Format cron à 5 champs : minute, heure, jour du mois, mois, jour de la semaine.",
    timeZoneLabel: "Fuseau horaire",
    history: "Historique des exécutions",
    historyHint: "Les 200 exécutions les plus récentes.",
    schedules: "Planifications",
    schedulesHint:
      "Deux exécutions d’une même planification ne peuvent pas se chevaucher.",
    empty: "Aucune exécution",
    emptyHint:
      "Lancez un job pour exécuter un script sur les ressources du projet.",
    emptySchedules: "Aucune planification",
    emptySchedulesHint:
      "Planifiez un job pour l’exécuter automatiquement, par exemple chaque matin.",
    deleteTitle: "Supprimer le job",
    deleteWarning:
      "L’exécution en cours sera interrompue et son historique supprimé.",
    launched: "Job lancé.",
    scheduled: "Planification enregistrée.",
  },

  apps: {
    usage: "Consultation",
    usageHint:
      "Qui a consulté cette application, sur les {days} derniers jours.",
    usageViews: "Consultations",
    usageVisitors: "Visiteurs identifiés",
    usageLastView: "Dernière consultation",
    usageAnonymous: "dont {count} anonymes",
    usageEmpty: "Personne n’a encore consulté cette application.",
    logsStarting:
      "L’application démarre : ses dépendances s’installent. Les journaux arrivent.",
    title: "Applications",
    subtitle: "Applications web servies depuis ce projet.",
    create: "Nouvelle application",
    createTitle: "Créer une application",
    createHint:
      "Choisissez un framework : la commande de lancement est générée pour vous.",
    frameworkLabel: "Framework",
    frameworkHint: "Détermine la commande de lancement et le port par défaut.",
    nameLabel: "Nom de l’application",
    namePlaceholder: "Tableau de bord commercial",
    slugLabel: "Adresse publique",
    slugHint:
      "Utilisée dans l’URL de l’application. Lettres minuscules, chiffres et tirets.",
    entrypointLabel: "Fichier principal",
    entrypointHint: "Chemin relatif à la racine du projet.",
    portHint:
      "Port sur lequel l’application écoute. Renseigné automatiquement selon le framework.",
    commandLabel: "Commande de lancement",
    commandHint:
      "Générée à partir du framework. Modifiable si votre application a besoin d’options particulières.",
    accessLabel: "Qui peut accéder à l’application",
    accessPrivate: "Membres du projet uniquement",
    accessOrganization: "Toute mon organisation",
    accessPublic: "Toute personne authentifiée",
    empty: "Aucune application",
    emptyHint:
      "Publiez une application Streamlit, Dash ou Shiny pour partager vos résultats sans donner accès au code.",
    restart: "Redémarrer",
    stop: "Arrêter",
    publish: "Publier en production",
    publishTitle: "Publier l’application",
    publishHint:
      "Une révision figée est créée et servie en production. Vous pourrez revenir à une révision précédente à tout moment.",
    revisions: "Révisions",
    revisionsHint: "Chaque publication crée une révision restaurable.",
    rollback: "Restaurer cette révision",
    rollbackTitle: "Restaurer la révision {number}",
    rollbackWarning:
      "La révision actuellement publiée sera remplacée. Aucune donnée n’est perdue.",
    restarts: "Redémarrages",
    deleteTitle: "Supprimer l’application",
    deleteWarning:
      "L’application sera arrêtée et son adresse publique libérée.",
  },

  apis: {
    title: "API",
    subtitle:
      "Des points d’appel déployés depuis vos projets, consommés par d’autres applications du système d’information.",
    create: "Déployer une API",
    callTitle: "Appeler cette API",
    callHint:
      "L’adresse, les extraits prêts à coller, et un jeton qui appartient au projet — il survit au départ de celui qui l’a créé.",
    tokenName: "Nom du jeton",
    tokenNameHint:
      "Le nom sert à le révoquer sans deviner : « scoring-prod » vaut mieux que « jeton 3 ».",
    tokenOnce:
      "Ce jeton est affiché une seule fois et n’est pas récupérable. Copiez-le maintenant.",
    tokenLastUsed: "utilisé {when}",
  },
  dashboards: {
    title: "Dashboards",
    subtitle: "Tableaux de bord publiés avec leur propre adresse.",
    create: "Nouveau dashboard",
    createTitle: "Créer un dashboard",
    createHint:
      "Un dashboard est une application dédiée à la visualisation, avec une adresse stable que vous pouvez partager.",
    empty: "Aucun dashboard",
    emptyHint:
      "Créez un dashboard pour partager des résultats avec des personnes qui n’ouvrent pas de notebook.",
  },

  environments: {
    buildNeedsProject: "Choisissez le projet de construction au-dessus : une construction consomme son quota et lui est attribuée.",
    buildProject: "Projet de construction",
    buildProjectHint:
      "L’image est construite dans ce projet et consomme son quota. L’environnement, lui, reste utilisable par tous les projets auxquels vous avez accès.",
    buildProjectPlaceholder: "Choisissez un projet",
    title: "Environnements",
    subtitle:
      "Images reproductibles utilisées par vos workspaces, jobs et applications.",
    create: "Nouvel environnement",
    createTitle: "Créer un environnement",
    createHint:
      "Partez d’un environnement existant ou d’une définition vierge, puis ajoutez vos bibliothèques.",
    nameLabel: "Nom de l’environnement",
    namePlaceholder: "analyse-python",
    nameHint: "Lettres minuscules, chiffres et tirets.",
    baseLabel: "Point de départ",
    baseEmpty: "Définition vierge",
    baseHint: "Dupliquer un environnement existant conserve ses bibliothèques.",
    definition: "Définition",
    definitionHint:
      "Le fichier qui décrit les bibliothèques installées dans l’environnement.",
    build: "Construire une révision",
    revisions: "Révisions",
    deleteRevision: "Supprimer cette révision",
    deleteRevisionHint:
      "Retire {image} de l'historique de cet environnement. L'image reste dans le registre.",
    revisionDeleted: "Révision supprimée",
    systemRebuildHint:
      "Cet environnement est celui que la plateforme est configurée pour faire tourner. Le reconstruire pousserait dans le dépôt de la plateforme, qu'elle ne démarrera pas — construisez sous un nom à vous.",
    revisionsHint: "Chaque construction produit une révision utilisable.",
    vulnerabilities: "Vulnérabilités",
    noVulnerabilities: "Aucune",
    notScanned: "Non scanné",
    scanUnavailable: "Inconnu",
    provenance: "Source",
    provenanceUnknown: "Inconnue",
    revisionNumber: "Révision {number}",
    activeRevision: "Révision active",
    empty: "Aucun environnement",
    emptyHint:
      "Un environnement fige les bibliothèques dont votre équipe a besoin, pour que tout le monde exécute le même code.",
    deleteTitle: "Supprimer l’environnement",
    deleteWarning:
      "Les workspaces et jobs qui l’utilisent ne pourront plus démarrer. Les exécutions en cours ne sont pas interrompues.",
    forkHint:
      "Ajoutez ici ce que cet environnement ajoute à l’image de base, par exemple : RUN pip install --no-cache-dir pandas",
    buildOutput: "Sortie de la construction",
    buildRunning: "construction en cours…",
    buildStarted: "Construction lancée. Vous pouvez suivre son avancement ici.",
  },

  ontologyCard: {
    title: "Ce que décrit cette ontologie",
    hint: "Le scan compte ce qu'il trouve. Ce que la donnée signifie — à quoi elle sert, ce pour quoi elle ne doit pas servir, sous quel droit elle est détenue, à qui demander — ne se déduit d'aucun octet : écrivez-le.",
    declare: "Décrire",
    edit: "Modifier",
    undeclared:
      "Personne n'a encore décrit cette ontologie. Un champ sans réponse est une information : mieux vaut vide que rempli d'une supposition.",
    placeholder:
      "Ce que contient cette donnée, à quoi elle sert, ce pour quoi elle ne doit pas servir, sous quel droit elle est détenue, à qui demander.",
    versionLine: "Version {version}, déclarée par {who}.",
    measured: "Ce que la plateforme a mesuré",
    records: "Fichiers",
    unreadable: "{count} fichier(s) hors de la règle de lecture : comptés dans la source, absents des extraits.",
    methodPathScan: "scan des chemins",
    measuredBy: "Compté par un {method} du {when}.",
    neverMeasured: "Aucun scan n'a encore mesuré cette ontologie.",
    structureNever: "Contenu des fichiers jamais vérifié",
    identifiersFound: "Identifiants présents dans les fichiers",
    identifiersNone: "Aucun identifiant trouvé",
    structureBy: "{method}, {count} champs vérifiés, le {when}.",
  },
  datasets: {
    deleteWarningOntologies:
      "{ontologies} ontologie(s) décrivent ce dataset, portant {extracts} extrait(s). Elles ne seront pas supprimées : elles resteront au catalogue en décrivant une source qui n’existe plus, et le premier signe sera un scan qui ne retrouve pas son bucket.",
    editTitle: "Modifier le jeu de données",
    editHint:
      "Le nom et la description. L'emplacement de stockage ne se change pas ici : le serveur ne sait pas encore le faire, et les montages déjà créés le portent gelé.",
    storageFixed: "Stockage (non modifiable)",
    storageFixedHint:
      "Adresse et compartiment. Pour en changer, il faut recréer le jeu de données, ce qui perd les habilitations accordées dessus.",
    title: "Datasets",
    subtitle: "Jeux de données stockés sur S3 et partagés entre projets.",
    create: "Nouveau dataset",
    createTitle: "Créer un dataset",
    createHint:
      "Créez un espace de stockage géré par la plateforme, ou rattachez un bucket S3 existant.",
    modeLabel: "Type de stockage",
    modeLocal: "Stockage géré par la plateforme",
    modeLocalHint: "La plateforme crée et gère le bucket pour vous.",
    modeExternal: "Bucket S3 existant",
    modeExternalHint: "Vous fournissez l’adresse et des identifiants d’accès.",
    classificationLabel: "Classification",
    classificationStandard: "Standard",
    classificationHds: "Données de santé (HDS)",
    classificationHint:
      "La classification HDS restreint l’aperçu, le téléchargement et l’export.",
    ownerOrganizationLabel: "Organisation propriétaire",
    ownerOrganizationPlaceholder: "Choisir une organisation",
    hdsWarning:
      "La création et l’attribution de datasets HDS sont réservées aux administrateurs. L’aperçu est limité et le téléchargement direct est désactivé.",
    endpointLabel: "Adresse du service S3",
    bucketLabel: "Nom du bucket",
    prefixHint: "Restreint le dataset à un sous-dossier du bucket.",
    credentialsHint:
      "Les identifiants sont chiffrés et ne sont jamais réaffichés. Utilisez des identifiants dédiés, avec les droits minimum nécessaires.",
    empty: "Aucun dataset",
    emptyHint:
      "Créez un dataset pour stocker vos fichiers et les partager avec votre équipe.",
    files: "Fichiers",
    filesCount: "Fichiers",
    inThisFolder: "Dans ce dossier : {files} fichiers, {size}",
    totalSize: "Volume total",
    explorer: "Explorateur de fichiers",
    upload: "Téléverser",
    dropHere: "Déposer dans {path}",
    dropProgress: "Dépôt {done}/{total} — {name}",
    dropDone: "{count} fichier(s) déposé(s).",
    dropPartly: "{ok} déposé(s), {failed} en échec :",
    uploadTitle: "Ajouter des fichiers",
    uploadPathLabel: "Dossier de destination",
    uploadFileLabel: "Fichier",
    newFolder: "Nouveau dossier",
    move: "Déplacer",
    moveDone: "{count} élément(s) déplacé(s).",
    moveFailed: "Déplacement impossible : {names}",
    moveHint: "Glissez une ligne sur un dossier pour l’y déplacer.",
    rename: "Renommer",
    renameNewName: "Nouveau nom",
    renameHint:
      "Le fichier reste dans ce dossier. La copie est verifiee avant que l'ancien nom soit retire : en cas d'echec, les deux noms subsistent plutot qu'aucun.",
    objectRenamed: "Objet renomme",
    folderNameLabel: "Nom du dossier",
    emptyFolder: "Ce dossier est vide",
    emptyFolderHint: "Téléversez un premier fichier pour commencer.",
    downloadSelection: "Télécharger la sélection",
    downloadDisabled:
      "Cette plateforme n’autorise pas la sortie des fichiers vers un poste local. Les fichiers restent consultables ici.",
    deleteSelection: "Supprimer la sélection",
    selectedCount: "{count} élément sélectionné",
    selectedCountPlural: "{count} éléments sélectionnés",
    permissions: "Permissions",
    replaceCredentials: "Remplacer les identifiants",
    replaceCredentialsHint:
      "Éprouvés sur le bucket avant d'être enregistrés. Des identifiants qui ne l'atteignent pas sont refusés et les actuels restent en place.",
    accessKey: "Clé d'accès",
    credentialsReplaced: "Identifiants remplacés",
    ownershipHint:
      "Le propriétaire administre le dataset et ses droits. Une organisation survit au départ de la personne qui l’a créé.",
    permissionsHint:
      "Le propriétaire gère la ressource. Writer : lecture et écriture. Reader : lecture seule.",
    grant: "Accorder un accès",
    revoke: "Révoquer l’accès",
    storage: "Stockage",
    classification: "Classification",
    deleteTitle: "Supprimer le dataset",
    deleteWarning:
      "Le dataset et ses métadonnées sont supprimés. Pour un bucket externe, les fichiers restent dans votre bucket.",
    deleteObjectsTitle: "Supprimer les fichiers sélectionnés",
    deleteObjectsWarning: "Cette action est irréversible.",
    deleteFoldersWarning:
      "{folders} dossier(s) et {files} fichier(s) sélectionnés. Un dossier est supprimé avec tout ce qu'il contient, y compris ce que cet écran n'affiche pas. Cette action est irréversible.",
    uploadDone: "Fichier téléversé.",
    uploadFailed: "Échec du téléversement",
  },

  datasources: {
    title: "Sources de données",
    subtitle:
      "Connexions vers des bases et services de données, rattachables à vos projets.",
    create: "Nouvelle source",
    createTitle: "Ajouter une source de données",
    createHint:
      "Connectez une base existante, ou déployez un service géré par la plateforme.",
    permissions: "Qui peut utiliser ce connecteur",
    permissionsHint:
      "Prêter la connexion sans s'en dessaisir. Le lecteur l'ouvre depuis un workspace, une app ou un job ; le rédacteur peut aussi en modifier la définition.",
    grant: "Prêter",
    revoke: "Retirer l'accès",
    typeLabel: "Type de source",
    actLabel: "Que voulez-vous faire ?",
    actHint: "Les deux produisent une source rattachable à un projet.",
    actConnect: "Connecter une base existante",
    actConnectHint: "Vous donnez où elle est et avec quoi s’y connecter",
    actCreate: "Créer une base",
    actCreateHint: "La plateforme la fait tourner et génère ses identifiants",
    engineLabel: "Moteur",
    storageLabel: "Volume",
    storageHint: "Taille du disque persistant, par exemple 10Gi.",
    tierLabel: "Gabarit matériel",
    tierHint: "Ce que la base reçoit comme processeur et mémoire.",
    manage: "Gérer la source",
    definition: "Définition",
    definitionHint: "Ce vers quoi pointe la source. Changer l’hôte redirige tous ceux qui la montent : il faut Écriture dessus, ou sa propriété.",
    definitionManagedHint: "Cette base est gérée par la plateforme : son hôte, son port, sa base, son utilisateur et son mot de passe sont les siens. Seul son nom se change ici.",
    definitionSaved: "Définition enregistrée",
    databaseLabel: "Base de données",
    usernameLabel: "Utilisateur",
    passwordSecretLabel: "Secret contenant le mot de passe",
    passwordSecretHint:
      "Sélectionnez un secret existant plutôt que de saisir le mot de passe ici.",
    sslLabel: "Mode SSL",
    empty: "Aucune source de données",
    emptyHint:
      "Ajoutez une connexion pour interroger une base depuis vos workspaces et vos jobs.",
    test: "Tester la connexion",
    testOk: "Connexion établie.",
    testFailed: "Connexion impossible",
    restart: "Redémarrer le service",
    attached: "Projets rattachés",
    attach: "Rattacher au projet",
    detach: "Détacher du projet",
    managed: "Service géré",
    external: "Connexion externe",
    deleteTitle: "Supprimer la source de données",
    deleteWarning: "Les projets rattachés perdront l’accès à cette source.",
  },

  mounts: {
    title: "Projets",
    hint: "Un objet du catalogue se monte dans autant de projets qu’on veut : c’est un lien, pas une appartenance. Le détacher retire le montage, jamais l’objet.",
    none: "—",
    attach: "Rattacher",
    detach: "Détacher",
    attached: "Rattaché au projet.",
    detached: "Détaché du projet.",
    noProject: "Vous n’êtes membre d’aucun projet.",
    hidden: "+{count} non visible(s)",
    hiddenHint:
      "Monté aussi dans {count} projet(s) dont vous n’êtes pas membre, ou dont le projet a été supprimé.",
  },

  ontologies: {
    describe: "Décrire la mise en page",
    describePrompt:
      "Aide-moi à décrire comment « {name} » est rangé, à partir des formes de chemins : quel niveau porte le regroupement, lequel porte le temps, lequel porte la catégorie — et comment ce métier appelle ces trois choses, en un mot chacune (par exemple patient / visite / modalité, ou client / mois / opération).",
    title: "Ontologies",
    subtitle: "Vos objets métier au-dessus des données brutes.",
    empty: "Aucune ontologie",
    emptyHint:
      "Une ontologie décrit vos objets métier au-dessus des données. Noryx en construit l’inventaire en lisant les chemins des objets d’un dataset — jamais leur contenu : ce qui les regroupe, quand, et de quelle sorte. Ce que la donnée signifie se déclare sur l’ontologie.",
    scanTitle: "Analyser une source",
    scanHint:
      "Le scan lit les chemins des objets du dataset — jamais leur contenu — et en déduit un inventaire : ce qui regroupe les objets, quand, de quelle sorte, dans quels formats et quel volume. C’est une photographie, pas une description : ce que la donnée signifie — sa finalité, ce pour quoi elle ne doit pas servir, le droit sous lequel elle est détenue — se déclare sur l’ontologie, et les chiffres que la plateforme a pris elle-même se placent à côté. Scanner une source déjà décrite met à jour son ontologie plutôt que d’en ajouter une seconde ; les extraits déjà déclarés gardent leur liste de fichiers.",
    describeSent:
      "Question envoyée à l’assistant — ouvrez le panneau pour lire la réponse.",
    scanDataset: "Dataset",
    scanDone: "Ontologie créée : {objects} objets, {subjects} {entities}.",
    scanRefreshed:
      "Ontologie mise à jour : {objects} objets, {subjects} {entities}. Les extraits déjà déclarés gardent leur liste de fichiers.",
    scanNoDataset:
      "Aucun dataset attaché à ce projet. Attachez-en un depuis l’onglet Données du projet.",
    scan: "Analyser la source",
    object: "Objet",
    objects: "Objets",
    noMatch: "Aucun objet ne correspond à ce filtre.",
    source: "Source",
    profile: "Profil d’inférence",
    reading: "Lecture",
    readingUnrecorded: "Non enregistrée",
    readingUnrecordedHint: "Cette photographie est antérieure à l’enregistrement de la règle : on ne sait pas comment elle a été lue. Un nouveau scan le dira.",
    readingPending: "Cette photographie n’a pas été prise avec la règle actuelle.",
    readingPendingHint: "La source déclare maintenant « {rule} ». Relancez un scan pour que les chiffres et les mots de cette page suivent.",
    readingDeclared: "Déclarée",
    readingDefault: "Par défaut",
    completeness: "Couverture",
    completenessHint:
      "Combien d’{entities} portent chaque {category}, et lesquelles ne l’ont pas. Un extrait demandé par {category} exclut silencieusement les secondes.",
    completenessSubjects:
      "{complete} {entities} sur {total} portent toutes les {categories}.",
    completenessModality: "Catégorie",
    completenessWithout: "{entities} sans cette {category}",
    patternRecognisedFor: "{entities} reconnues",
    completenessHolders: "Sujets",
    completenessMissing: "Sujets sans cette modalité",
    extracts: "Extraits",
    extractDeclare: "Déclarer un extrait",
    extractPickOntology: "Choisir une ontologie",
    extractsNone: "Aucun extrait n’est tiré de cette ontologie.",
    extractsCount: "{count} extrait(s) sont tirés de cette ontologie.",
    extractsHint:
      "Un extrait, c’est ce que vous montez dans un workspace : l’ontologie dit ce qu’il y a, l’extrait dit ce que vous emportez. Il fige la liste des fichiers au moment où vous le déclarez, donc la même question posée le mois prochain désigne toujours la même étude. Rien n’est dupliqué — le montage crée un arbre de liens vers le dataset.",
    extractCreate: "Déclarer un extrait",
    extractModalitiesUnknown:
      "Aucune catégorie mesurée sur cette ontologie — l’extrait prendra tout.",
    layoutLevel: "Dossier {rank}",
    layoutDrop: "Retirer",
    layoutAdd: "Ajouter un dossier : {name}",
    layoutLeaf: "fichiers…",
    layoutShortWarning: "Un niveau en moins regroupe ce qu’il séparait. Si deux fichiers se retrouvent au même endroit, la déclaration est refusée en nommant lesquels.",
    extractFrom: "Découpé dans l’ontologie « {name} », telle qu’elle a été lue le {when}.",
    extractFromFigures: "{entities} {entityName}, {files} fichiers, {categories} {categoryName}.",
    extractWhat: "1 · Ce que vous emportez",
    extractWhatHint: "Par défaut, tout. Cochez pour restreindre.",
    extractKinds: "Genres de fichier",
    extractKindsAll: "Ne prendre que ces genres. Rien de coché : tous.",
    extractKindsChosen: "Les autres genres resteront dans la source.",
    extractKindWeight: "{objects} fichiers, {size}",
    extractOwnershipHint: "Le propriétaire décide qui peut lire l'extrait et qui peut le supprimer.",
    permissions: "Qui peut lire cette ontologie",
    permissionsHint: "Prêter la lecture d'une étude sans transférer l'ontologie.",
    grant: "Prêter",
    revoke: "Retirer l'accès",
    extractPermissions: "Qui peut lire cet extrait",
    extractPermissionsHint: "Prêter la sélection sans prêter l'étude dont elle sort, et sans s'en dessaisir.",
    extractGrant: "Prêter",
    extractRevoke: "Retirer l'accès",
    extractDataTypes: "Dossiers de la modalité",
    extractDataTypesAll: "Ne prendre que ces dossiers. Rien de coché : tous.",
    extractDataTypesChosen: "Les autres dossiers resteront dans la source.",
    extractTables: "Tables de mesure",
    extractTablesAll: "Ne prendre que ces tables. Rien de coché : toutes.",
    extractTablesChosen: "Les autres tables resteront dans la source.",
    extractPickCategories: "Ne prendre que ces {categories}. Rien de coché : toutes.",
    extractPickEntities: "Ne prendre que ces {entities}, séparées par des virgules. Vide : toutes.",
    extractTakes: "Cet extrait figera {files} fichiers sur {total}.",
    extractTakesAtMost: "Cet extrait figera au plus {files} fichiers sur {total} — le filtre par entité réduira encore.",
    extractHow: "2 · Comment vous le retrouverez",
    extractHowHint: "L’ordre des dossiers dans le workspace. Les mêmes fichiers, rangés pour la question que vous posez.",
    layoutUnnamed: "nom-de-l-extrait",
    extractLayout: "Disposition",
    pattern: "Lecture de la source",
    scanDeclareRule: "Aucun sujet reconnu : déclarez la règle de lecture de ce dataset, dans Paramètres puis Règle de lecture. Formes rencontrées :",
    patternRule: "Règle de lecture",
    scanChangedTitle: "Ce qui a changé",
    scanChangedReading:
      "La lecture de la source a changé : {objectsBefore} → {objectsAfter} objets, {subjectsBefore} → {subjectsAfter} {entities}. C’est la règle qui explique l’écart, pas la donnée.",
    scanChangedData:
      "La source a bougé, à lecture inchangée : {objectsBefore} → {objectsAfter} objets, {subjectsBefore} → {subjectsAfter} {entities}.",
    patternRuleDefaultPlain: "Règle par défaut : le premier niveau qui ressemble à un identifiant, puis les deux suivants",
    patternLevelIs: "niveau {level} = {name}",
    patternShapesMore: "Voir les {count} autres formes",
    patternShapesFewer: "Replier",
    settings: "Réglages",
    settingsHint: "Comment la source est lue, l’exploration des objets, et le propriétaire.",
    scanHistory: "Historique des scans",
    scanHistoryHint: "Chaque photographie de la source, la plus récente en tête, avec la règle qui l’a produite. Un écart s’explique soit par la donnée qui a bougé, soit par la lecture qui a changé.",
    scanWhen: "Scanné le",
    scanChanged: "Écart",
    scanFirst: "Premier scan",
    scanUnchanged: "Rien n’a bougé",
    scanDelta: "{count} {what}",
    scanReadingChanged: "Lecture modifiée",
    content: "Explorer le contenu",
    contentHint: "Une ligne par {entity} × {period} × {category}, avec le nombre de fichiers. C’est le détail derrière les chiffres de la fiche.",
    contentFilter: "Filtrer",
    contentFilterHint: "Cherche le même mot dans les trois colonnes. Vide, tout s’affiche.",
    contentApply: "Filtrer",
    contentEmpty: "Cette ontologie n’a reconnu aucun fichier.",
    contentCount: "{shown} ligne(s) affichée(s) sur {total}.",
    proposalPaste: "Coller la proposition de l’assistant",
    proposalPasteHint: "Colle sa réponse entière. Les trois niveaux et les trois mots remplissent le formulaire ci-dessous — rien n’est enregistré avant que tu le fasses.",
    proposalApply: "Lire la proposition",
    proposalRead: "Niveaux lus : {levels} — mots : {names}. Vérifie, essaie, puis enregistre.",
    proposalNoNames: "aucun",
    proposalUnreadable: "Aucune lecture trouvée. Attendu : « subject: level 1, visit: level 2, modality: level 3 », et si possible « names: patient / visite / modalité ».",
    patternProducedWith: "Produite avec la règle :",
    scanRuleDeclared: "Ce dataset déclare sa lecture :",
    scanRuleWhereToChange:
      "— modifiable sur l’ontologie, carte « Lecture de la source ».",
    scanRuleDefault: "Ce dataset ne déclare pas de lecture.",
    scanRuleDefaultHint:
      "La règle par défaut s’applique : elle cherche un segment qui ressemble à un identifiant, puis prend les deux suivants comme niveau temporel et catégorie. Si le scan ne reconnaît rien, déclarez la lecture sur le dataset et relancez.",
    patternAsk: "Demander une proposition",
    patternSubject: "Niveau 1 lu",
    patternVisit: "Niveau 2 lu",
    patternModality: "Niveau 3 lu",
    patternRuleHint:
      "Le niveau d’un chemin, compté depuis zéro. Pour `SELENA/SELENA-01-001/20260218/ANTERION/…` : sujet 1, visite 2, modalité 3. Laissez vide un niveau que la source ne porte pas. Essayez avant d’enregistrer.",
    patternCompiled: "Règle par défaut de la plateforme",
    patternNamesHint: "Comment ce métier nomme ses trois niveaux. Vides, la plateforme dit « entité », « période », « catégorie ».",
    patternSubjectName: "Nom du regroupement",
    patternVisitName: "Nom du niveau temporel",
    patternModalityName: "Nom de la catégorie",
    patternSubjectLevel: "Niveau du regroupement",
    patternVisitLevel: "Niveau temporel",
    patternDataTypeLevel: "Niveau du type de données (facultatif)",
    patternDataTypeHint: "Le dossier sous la modalité. Vide : la modalité est le dernier niveau.",
    patternDataTypeName: "Nom du type de données",
    patternDataTypeWord: "type de données",
    patternModalityLevel: "Niveau de la catégorie",
    patternTry: "Essayer sur de vrais chemins",
    patternSave: "Enregistrer la règle",
    patternSaved: "Règle enregistrée. Relancez un scan pour l’appliquer.",
    patternClear: "Oublier la règle",
    patternCleared: "Règle oubliée : la source revient à la règle par défaut.",
    patternTrialResult: "La règle reconnaît {recognised} chemin(s) sur les {sampled} essayés. Voici ce qu’elle lit sur chacun — un niveau décalé se voit ici, pas après le scan.",
    extractWhole: "Monter l’ontologie entière",
    extractWholeSuffix: "tout",
    extractWholeHint:
      "Une ontologie décrit une source, elle ne la contient pas : ce qui se monte est toujours un extrait. Celui-ci est le cas particulier sans filtre — toute l’ontologie.",
    patternHint:
      "Comment la plateforme lit les chemins de cette source : quelle position porte le sujet, la visite, la modalité. C’est ce que l’assistant propose quand on lui montre les formes.",
    patternRecognised: "Formes qui ont produit un sujet",
    patternUnrecognised: "Formes qui n’ont rien produit",
    patternNone:
      "Aucune forme reconnue : cette source n’est pas rangée comme le profil l’attend. Demandez une lecture à l’assistant.",
    patternDirectories:
      "{count} clé(s) de dossier ignorée(s) : S3 n’a pas de dossiers, les compter gonflerait le n.",
    extractEmpty: "Aucun extrait pour cette ontologie.",
    extractCatalogEmpty: "Aucun extrait",
    extractCreated: "Extrait figé sur {count} fichiers.",
    extractDeleteTitle: "Supprimer l’extrait",
    extractDeleteWarning:
      "La sélection figée est perdue. Les fichiers du dataset ne sont pas touchés.",
    extractDelete: "Supprimer l’extrait",
    extractRename: "Renommer l’extrait",
    level_subject: "sujet",
    level_visit: "visite",
    level_modality: "modalité",
    extractDetailHint:
      "La liste de fichiers figée au moment de la déclaration. C’est elle qui fait qu’un n se rejoue à l’identique dans deux ans.",
    extractWholeBadge: "Toute l’ontologie",
    extractSubjectCount: "{count} sujet(s)",
    extractFilesShown: "{shown} fichiers affichés sur {total}.",
    extractCheckFiles: "Vérifier les fichiers",
    extractCheckHint:
      "Figer protège le n, pas les octets : un fichier supprimé de la source se monte en lien cassé.",
    extractChecking: "Vérification…",
    extractCheckFailed: "La source n’a pas pu être lue.",
    extractCheckAllPresent:
      "Échantillon de {checked} fichiers sur {total} : tous présents.",
    extractCheckMissing:
      "{missing} fichier(s) manquant(s) sur les {checked} vérifiés — la source a été élaguée.",
    extractRenameHint:
      "Le libellé seul change. La liste de fichiers figée, son auteur et ses dates ne bougent pas : un n déjà publié sous ce nom décrit toujours les mêmes fichiers.",
    extractDeleted: "Extrait supprimé.",
    extractCatalogHint:
      "Un extrait est un sous-ensemble figé d’une ontologie : une liste de fichiers arrêtée au moment où on la déclare. Il se cède et se rattache à un projet comme un dataset. Déclarez-en un depuis l’ontologie dont il est tiré.",
    extractMountHint:
      "Monté dans les workspaces du projet sous /extracts/<nom>/, les niveaux dans l’ordre que cet extrait a déclaré.",
    freshness: "Fraîcheur",
    freshnessMeasured: "Mesurée il y a {days} jours, sur {objects} objets.",
    freshnessMeasuredToday: "Mesurée aujourd’hui, sur {objects} objets.",
    freshnessAligned: "La source contient toujours le même nombre d’objets.",
    freshnessGrown:
      "La source contient {drift} objets de plus depuis cette mesure.",
    freshnessShrunk:
      "La source contient {drift} objets de moins depuis cette mesure.",
    freshnessUnknown:
      "Source non consultée : impossible de dire si cette mesure est toujours juste.",
    freshnessStale: "Cette ontologie ne décrit plus sa source",
    ownershipHint:
      "Le propriétaire administre l’ontologie et ses droits. Une organisation survit au départ de la personne qui l’a créée.",
    rename: "Nom et description",
    renameHint:
      "Le nom vient du scan, qui le lit sur la source. Le corriger ne touche ni les objets scannés ni les extraits : seul ce qui s’affiche change.",
    deleteTitle: "Supprimer l’ontologie",
    deleteWarning: "Les projets rattachés perdront l’accès à ce modèle.",
    deleteWarningExtracts:
      "Cette ontologie porte {count} extrait(s) : ils seront détruits avec elle. Une sélection figée ne se reconstruit pas à l’identique — un nouveau scan sur une source qui a grandi donne un autre n. Pour relire la source, relancez plutôt un scan : il met l’ontologie à jour sans la supprimer.",
    deleteWarningUnknown:
      "Impossible de savoir ce que cette suppression emporte. Les extraits déclarés sur cette ontologie seraient détruits avec elle.",
  },

  repositories: {
    createdUnreachable: "Dépôt ajouté, mais la vérification a échoué",
    title: "Dépôts Git",
    subtitle: "Dépôts clonés dans vos workspaces sous /repos.",
    create: "Nouveau dépôt",
    createTitle: "Ajouter un dépôt Git",
    createHint:
      "Le dépôt est cloné automatiquement dans /repos au démarrage de vos workspaces.",
    urlLabel: "Adresse du dépôt",
    tokenTooBroad: "Ce jeton peut aussi :",
    tokenTooBroadDanger: "Ce jeton peut détruire ou administrer :",
    urlHint:
      "Adresse HTTPS du dépôt. L’authentification se fait par jeton ; les adresses SSH ne sont pas encore prises en charge.",
    defaultRefLabel: "Branche par défaut",
    authTypeLabel: "Authentification",
    authPersonalToken: "Jeton personnel (PAT)",
    authRepositoryToken: "Jeton de dépôt (PRAT)",
    authNone: "Dépôt public",
    authSecret: "Jeton stocké dans un secret",
    authSecretLabel: "Secret contenant le jeton",
    authorNameLabel: "Nom pour les commits",
    authorEmailLabel: "Adresse e-mail pour les commits",
    empty: "Aucun dépôt Git",
    emptyHint:
      "Ajoutez un dépôt pour retrouver votre code dans chaque workspace du projet.",
    validate: "Vérifier l’accès",
    reachable: "Accessible",
    unreachable: "Inaccessible",
    lastValidated: "Dernière vérification",
    deleteTitle: "Supprimer le dépôt",
    deleteWarning: "Le dépôt ne sera plus cloné dans les nouveaux workspaces.",
  },

  secrets: {
    title: "Secrets",
    subtitle: "Valeurs sensibles injectées dans vos exécutions.",
    create: "Nouveau secret",
    createTitle: "Créer un secret",
    createHint:
      "La valeur est chiffrée et ne sera plus jamais affichée après enregistrement.",
    nameLabel: "Nom du secret",
    nameHint: "Injecté dans vos exécutions sous la forme NORYX_SECRET_{name}.",
    namePlaceholder: "api_partenaire",
    valueLabel: "Valeur",
    valueHint: "Elle ne sera plus affichée après enregistrement.",
    kindLabel: "Nature du secret",
    kindHint:
      "Un jeton a une durée de vie décidée ailleurs ; la plateforme prévient avant qu’il n’expire, à condition de connaître la date.",
    kindGeneric: "Valeur quelconque",
    kindPersonalToken: "Jeton personnel (PAT)",
    kindRepositoryToken: "Jeton de dépôt (PRAT)",
    expiresRequiredHint: "GitHub et GitLab plafonnent leurs jetons à un an.",
    neverExpires: "Ce jeton n’expire pas",
    expiryAnswerRequired:
      "Indiquez la date d’expiration, ou cochez que le jeton n’expire pas.",
    expiresLabel: "Date d’expiration",
    expiresHint:
      "Optionnelle. Un secret expiré n’est plus injecté dans les exécutions.",
    empty: "Aucun secret",
    emptyHint:
      "Stockez ici vos jetons et mots de passe plutôt que de les écrire dans votre code.",
    expiringSoon: "Expire bientôt",
    deleteTitle: "Supprimer le secret",
    deleteWarning:
      "Les exécutions qui l’utilisent échoueront au prochain démarrage.",
    created: "Secret enregistré.",
  },

  production: {
    title: "Production",
    subtitle: "Inventaire des services actuellement publiés sur la plateforme.",
    published: "Services publiés",
    running: "En marche",
    public: "Accessibles largement",
    revision: "Révision",
    access: "Accès",
    publishedAt: "Publié le",
    publishedBy: "Publié par",
    empty: "Aucun service publié",
    emptyHint:
      "Publiez une application depuis un projet pour la voir apparaître ici.",
    revisionHistory: "Historique des révisions",
    selectService: "Sélectionnez un service pour voir ses révisions.",
  },

  members: {
    orgTitle: "Accès par organisation",
    orgHint:
      "Chaque membre de l’organisation obtient ce rôle sur le projet. Les accès s’additionnent : c’est le plus fort qui s’applique, entre le rôle personnel et celui hérité d’une organisation.",
    orgEmpty: "Aucune organisation n’a d’accès à ce projet.",
    orgPlaceholder: "Choisissez une organisation",
    orgGrant: "Attribuer",
    orgGranted: "Accès attribué à l’organisation.",
    title: "Membres",
    subtitle: "Personnes ayant accès à ce projet et leur niveau de droits.",
    invite: "Inviter",
    inviteTitle: "Inviter une personne",
    inviteHint: "La personne doit déjà avoir un compte sur la plateforme.",
    userLabel: "Utilisateur",
    roleViewer: "Lecteur",
    roleBasedOn: "Hérite de {base}",
    roleViewerHint: "Consulte le projet et ses résultats, sans rien modifier.",
    roleEditor: "Contributeur",
    roleEditorHint:
      "Lance des workspaces et des exécutions, modifie les ressources.",
    roleAdmin: "Administrateur",
    roleAdminHint: "Gère les membres et les paramètres du projet.",
    empty: "Aucun membre",
    emptyHint:
      "Invitez vos collaborateurs pour travailler ensemble sur ce projet.",
  },

  network: {
    title: "Réseau et sécurité",
    subtitle:
      "Accès Internet sortant demandé par les projets et les utilisateurs.",
    profileLabel: "Profil d’accès",
    destinationLabel: "Destination",
    justificationLabel: "Justification",
    approve: "Approuver",
    reject: "Refuser",
    empty: "Aucune demande",
    emptyHint:
      "Les demandes d’accès Internet sortant apparaîtront ici pour validation.",
    pending: "En attente",
    approved: "Approuvée",
    rejected: "Refusée",
  },

  access: {
    title: "Accès",
    rule: "Posséder administre, a l’écriture et partage ; Écriture et Lecture sont des octrois sur l’objet, à une personne, une équipe ou une organisation. Appartenir à une organisation ne donne rien par soi-même.",
    connectorHint: "Lecture : l’utiliser dans un workspace et voir qui d’autre la détient. Écriture : changer aussi ce vers quoi elle pointe, ce qui redirige tous ceux qui la montent.",
    frozenHint: "Un extrait est gelé : il se lit, il ne s’écrit pas, même avec le droit d’écriture sur la donnée. Modifier un fichier demande le dataset source, attaché au projet. Le propriétaire partage et supprime l’extrait.",
    owner: "Propriétaire",
    ownerMeans: "administre, écrit, partage",
    ownerOrganizationMeans: "ses administrateurs nommés administrent, écrivent et partagent ; ses membres n’ont que ce qui leur est octroyé",
    right: "Droit",
    grant: "Donner l’accès",
    revoke: "Retirer l’accès",
    empty: "Aucun octroi",
    emptyHint: "Seul le propriétaire y accède. Donnez l’accès à une personne, une équipe ou une organisation.",
  },
  rbac: {
    matrix: "Matrice des droits",
    matrixHint:
      "Qui peut faire quoi, objet par objet. Posséder administre (transférer, accorder, supprimer) et ne donne pas la lecture ; lire ou écrire est un octroi sur l’objet, à une personne, une équipe ou une organisation ; une ligne « hérité » vient d’un tel octroi fait à un groupe. Ce qu’un projet attache n’apparaît pas ici : l’attacher ne donne aucun droit sur la donnée.",
    subject: "Sujet",
    resource: "Ressource",
    empty: "Aucun droit enregistré",
    emptyHint:
      "Les droits accordés sur les projets et les datasets apparaîtront ici.",
    organizations: "Organisations",
  },

  activity: {
    title: "Activité",
    subtitle: "Exécutions en cours sur la plateforme, tous projets confondus.",
    executions: "Exécutions actives",
    pods: "Pods",
    cpuReserved: "CPU réservé",
    memoryReserved: "Mémoire réservée",
    empty: "Aucune exécution en cours",
    emptyHint: "Les workspaces, jobs et applications actifs apparaîtront ici.",
    stopTitle: "Arrêter l’exécution",
    stopWarning: "L’exécution sera interrompue immédiatement.",
    kind: "Type",
  },

  quota: {
    title: "Quota du projet",
    hint: "Ce que ce projet peut faire tourner en même temps. Un lancement au-delà est refusé, en nommant la limite atteinte.",
    unlimited:
      "Aucune limite définie : ce projet peut lancer autant que le cluster le permet.",
    vcpu: "vCPU",
    memory: "Mémoire",
    workspaces: "Workspaces",
    jobs: "Traitements",
    noLimit: "illimité",
    zeroMeansNoLimit: "0 signifie aucune limite sur cette dimension.",
  },
  usage: {
    consumption: "Consommation",
    consumptionHint:
      "Ce que le projet a réellement consommé, mesuré toutes les cinq minutes et conservé cent jours.",
    restsOn: "Total établi sur {samples} mesures.",
    title: "Consommation par projet",
    hint: "Ce que chaque projet a réellement consommé sur les 30 derniers jours. Mesuré par échantillonnage toutes les 5 minutes ; une interruption de la plateforme n’est pas facturée.",
    vcpuHours: "vCPU·heures",
    memoryHours: "Gio·heures",
    peak: "Pic",
    samples: "Mesures",
  },
  people: {
    membershipGrantLabel: "Ce que l’appartenance donne sur ce que l’organisation possède",
    membershipGrantHint: "Écrit comme un octroi à l’organisation quand un objet est créé pour elle ou lui est transféré ; jamais rétroactif. « Rien » : ses administrateurs nommés décident objet par objet.",
    membershipGrantNone: "Rien",
    membershipGrantReader: "Lecture",
    membershipGrantWriter: "Lecture et écriture",
    membershipGrantSaved: "Politique enregistrée",
    serviceAccount: "Compte de service",
    serviceAccountHint:
      "Ce compte n est pas une personne : il n a pas de mot de passe, ne se connecte jamais, et agit par jeton.",
    title: "Personnes & organisations",
    hint: "Qui est qui : chaque organisation, ses equipes, et les personnes qui les composent. Tout se modifie depuis le panneau de droite.",
    newOrganization: "Nouvelle organisation",
    newOrganizationHint:
      "Une organisation est portee par l'annuaire : on la cree ici, on ne la supprime pas ici.",
    organizationCreated: "Organisation creee",
    alias: "Alias",
    organizationSummary: "{people} personne(s), {teams} equipe(s)",
    newTeam: "Nouvelle equipe",
    administrators: "Administrateurs de l'organisation",
    administratorsHint:
      "Qui peut transférer, renommer, supprimer ce que l'organisation possède, et décider qui le lit. C'est un droit, pas une appartenance : administrer n'ouvre aucun fichier.",
    administratorsEmpty:
      "Personne n'est nommé : ce que cette organisation possède n'est administré que par les administrateurs de la plateforme.",
    addToOrganization: "Ajouter a l'organisation",
    removeFromOrganization: "Retirer de l'organisation",
    teamSummary: "{count} membre(s)",
    teamRenamed: "Equipe renommee",
    teamRemoveWarning:
      "Supprimer l'equipe {name} ? Ses {count} membre(s) perdent l'acces qu'elle leur donnait, et ses octrois sur les projets partent avec elle.",
    teamRemoveHint:
      "{count} membre(s) concerne(s). Les octrois de l'equipe sur les projets sont retires avec elle.",
    unaffiliated: "Sans organisation",
    unaffiliatedHint:
      "Des comptes qui n'appartiennent a aucune organisation - typiquement des comptes de service, ou des personnes pas encore rattachees.",
    nobody: "Personne.",
    deactivated: "Desactive",
    neverSeen: "Jamais connecte",
    viewTree: "Arbre",
    viewList: "Liste",
    firstSeen: "Premiere connexion",
    presence_active: "Actif : s'est deja connecte",
    presence_never: "Jamais connecte",
    presence_disabled: "Desactive",
    administratorHint: "Peut agir sur le travail de tout le monde.",
    createUserIn: "Le compte sera cree dans {organization}.",
  },
  catalog: {
    subtitle:
      "Les actifs partagés entre projets — datasets, sources, ontologies, dépôts, environnements. Un projet y rattache ce dont il a besoin.",
  },
  projectData: {
    subtitle:
      "Ce que ce projet a rattaché depuis le catalogue. Rien n'est copié : détacher ne supprime rien.",
  },
  serviceAccounts: {
    title: "Comptes de service",
    subtitle:
      "Des comptes qui ne sont pas des personnes : ils appartiennent a une organisation, portent des roles, possedent ce qui doit survivre a ceux qui l ont mis en place, et ne se connectent jamais.",
    createTitle: "Creer un compte de service",
    createHint:
      "Pas de mot de passe, pas de connexion : ses identifiants sont des jetons, emis ici.",
    nameHint: "Le nom sous lequel l audit l enregistrera.",
    namePlaceholder: "for-production",
    purpose: "Ce qu il fait",
    purposePlaceholder: "Publie les tableaux de bord de l etude",
    responsible: "Responsable",
    responsibleHint: "La personne qui repond de ce compte. Obligatoire.",
    noResponsible: "personne",
    credentials: "Jetons vivants",
    noCredential: "aucun",
    issue: "Emettre un jeton",
    nextCredential: "Portee du prochain jeton",
    created: "Compte de service cree",
    disableTitle: "Desactiver",
    disableWarning:
      "{name} cessera d agir : ses jetons sont revoques puis le compte est desactive. Ce qu il possede reste a lui, et l audit garde sa trace.",
    disabled: "Compte desactive, {count} jeton(s) revoque(s)",
    empty: "Aucun compte de service",
    emptyHint:
      "Un compte de service porte une production que personne ne doit reprendre le jour ou son auteur part.",
  },

  componentCredentials: {
    rotate: "Renouveler",
    rotateWarning:
      "Un nouveau secret est emis pour {name} et l ancien cesse aussitot de fonctionner. Le nouveau ne s affiche qu une fois.",
    howToTitle: "Comment s en servir",
    howToHint:
      "Le secret s envoie en en-tete d autorisation. Il ne s affichera plus apres cette page.",
    identityHint:
      "Un compte de service n est pas un utilisateur : il n a ni nom ni compte, il agit au nom de la plateforme, et sa portee est ce qui le limite. L audit l enregistre sous le nom du composant.",
    scopeFullWarning:
      "La portee full ne limite rien : ce compte pourra tout faire sur la plateforme.",
    title: "Comptes de service",
    subtitle:
      "Les identifiants avec lesquels une automatisation s’authentifie : sauvegarde, validation, intégration continue. Ils n’appartiennent à personne et se révoquent un par un.",
    component: "Composant",
    componentHint:
      "Le nom du programme qui s’en sert. C’est lui qui apparaîtra dans le journal d’audit.",
    componentPlaceholder: "sauvegarde-nocturne",
    scopeHint:
      "Une portée par compte. Un composant qui en demande deux est deux comptes, et l’audit se lit.",
    createTitle: "Créer un compte de service",
    createHint: "Le secret ne s’affiche qu’une fois, à la création.",
    lastUsed: "Dernier usage",
    state_active: "actif",
    state_idle: "jamais utilisé",
    state_expired: "expiré",
    state_revoked: "révoqué",
    revokeWarning:
      "Le composant {name} ne pourra plus s’authentifier dès la révocation. Rien ne le préviendra.",
    empty: "Aucun compte de service",
    emptyHint:
      "Les composants de la plateforme et vos automatisations s’authentifient avec ces identifiants.",
  },

  admin: {
    visibleAsAdmin: "admin",
    visibleAsAdminHint:
      "Visible parce que vous administrez la plateforme : vous n’avez aucun droit propre sur cet objet.",
    deleteAccount: "Supprimer le compte",
    deleteHint:
      "La suppression retire définitivement le compte du fournisseur d’identité. Elle n’est pas réversible : réactiver ne sera plus possible.",
    deleteSecrets:
      "Ses secrets personnels sont supprimés : ils appartiennent à une personne, pas à un rôle, et ne s’héritent pas.",
    deleteAudit:
      "La piste d’audit conserve les événements produits par ce compte, sous son identifiant.",
    deletedHint: "Compte supprimé, {count} secret(s) personnel(s) effacé(s).",
    deactivate: "Désactiver le compte",
    deactivateHint:
      "Le compte ne pourra plus se connecter. Il n’est pas supprimé : la piste d’audit reste attribuable, et le compte peut être réactivé.",
    deactivateTokens:
      "Ses jetons d’API sont révoqués — un jeton agit comme son porteur, un compte désactivé dont les jetons fonctionnent encore ne l’est pas.",
    deactivateSecrets:
      "Ses secrets personnels ne sont pas transférés : ils appartiennent à la personne, pas au rôle.",
    deactivatedHint: "Compte désactivé, {count} jeton(s) révoqué(s)",
    reactivate: "Réactiver",
    ownedTitle: "Ce que ce compte possède",
    ownsNothing: "Ce compte ne possède rien en propre : rien à transférer.",
    ownedProjects: "Projets :",
    ownedDatasets: "Jeux de données :",
    ownedOntologies: "Ontologies :",
    ownedApps: "Applications :",
    ownedDatasources: "Sources de données :",
    ownedRepositories: "Dépôts :",
    ownedExtracts: "Extraits :",
    successorLabel: "Repreneur",
    successorHint:
      "Recevra la propriété de tout ce qui précède, dans la même opération.",
    smtp: "Serveur de messagerie",
    smtpHint:
      "Utilisé par la plateforme pour envoyer les liens d’invitation et de réinitialisation. Renseigné par installation : chaque site passe par son propre relais.",
    smtpConfigured: "Configuré",
    smtpMissing: "Absent",
    smtpFrom: "Adresse d’expédition",
    smtpFromHint:
      "Ce que verra le destinataire. Le relais doit accepter cette adresse.",
    smtpFromName: "Nom affiché",
    smtpReplyTo: "Répondre à",
    smtpUser: "Identifiant",
    smtpPassword: "Mot de passe",
    smtpPasswordHint:
      "Écrit seulement : il n’est jamais renvoyé à un navigateur.",
    smtpPasswordStored:
      "Un mot de passe est enregistré. Laisser vide pour le conserver.",
    smtpNoAuthHint:
      "Authentification désactivée : le relais accepte l’envoi sans identifiants.",
    smtpAuth: "Authentification",
    smtpTest: "Envoyer un test",
    smtpTestTo: "Destinataire du test",
    smtpTestHint:
      "Testez avant d’enregistrer : la configuration en place n’est pas remplacée.",
    smtpTestSent: "Message de test envoyé à {recipient}",
    resetByEmail: "Envoyer un lien",
    activity: "Activité",
    activityHint:
      "Qui s'est servi de la plateforme, et pour quoi. Compté sur ce que la plateforme a enregistré, pas sur ce dont on se souvient.",
    activityEmpty: "Rien d'enregistré sur cette période.",
    activityCoverage:
      "Les enregistrements commencent le {since} : la période antérieure n'est pas calme, elle n'est pas enregistrée.",
    byOrganization: "Par organisation",
    noOrganization: "Sans organisation",
    activePeoplePerDay: "Personnes actives par jour",
    whoWasActive: "{count} personnes actives",
    whatWasDone: "Ce qui a été fait",
    peopleCount: "{count} personnes",
    eventsCount: "{count} actions",
    actionsTruncated:
      "Les {shown} actions les plus fréquentes sur {total} types.",
    window7d: "7 derniers jours",
    window30d: "30 derniers jours",
    window90d: "90 derniers jours",
    administrator: "Admin",
    lastSeen: "Dernière connexion",
    resetByEmailHint:
      "Envoie à {user} un lien pour choisir son mot de passe, valable {hours} heures. Rien à dicter ni à coller.",
    resetByEmailSent: "Lien envoyé",
    teamUsage: "Consommation par équipe",
    teamUsageHint:
      "Sur les 30 derniers jours. La consommation est échantillonnée par projet : le chiffre d'une équipe est une attribution, pas une mesure.",
    teamUsageEmpty: "Aucun rapport de consommation disponible.",
    teamUsageNoTeams:
      "Aucune équipe n'atteint de projet : tout est non attribué.",
    teamUsagePlatform: "Plateforme (vCPU·h)",
    teamUsagePlatformHint: "Ce que le cluster a réellement consommé.",
    teamUsageAttributed: "Attribué (vCPU·h)",
    teamUsageAttributedHint: "La somme des lignes ci-dessous.",
    teamUsageOverlap:
      "Dépasse la plateforme de {excess} : des équipes partagent des projets.",
    teamUsageUnattributed: "Non attribué (vCPU·h)",
    teamUsageUnattributedHint:
      "Atteint par aucune équipe — détenu en propre ou par une organisation.",
    teamUsageMembers: "Membres",
    teamUsageProjects: "Projets",
    teamUsageVcpuHours: "vCPU·h",
    teamUsagePeak: "Pic vCPU",
    teamUsageShared: "{count} partagé(s)",
    teams: "Équipes",
    teamCreated: "Équipe créée",
    teamRemove: "Supprimer l'équipe",
    teamRemoved: "Équipe supprimée",
    teamMemberAdd: "Ajouter un membre",
    teamMemberPlaceholder: "Choisir une personne",
    teamMemberRemove: "Retirer de l'équipe",
    teamNoMembers: "Personne dans cette équipe.",
    hardwareTiers: "Gammes de machines",
    hardwareTiersHint:
      "Les tailles proposées au lancement d’un workspace, d’un job ou d’une application.",
    tierName: "Nom affiché",
    tierId: "Identifiant",
    tierIdHint:
      "Sert de référence dans l’API et les workspaces existants : il ne change pas.",
    tierRequest: "Réservé",
    tierLimit: "Plafond",
    tierRequestHint: "Ce que l’ordonnanceur met de côté pour la machine.",
    tierLimitHint: "Ce que la machine peut atteindre.",
    tierDefault: "Par défaut",
    tierAdd: "Ajouter une gamme",
    tierSaved: "Gamme enregistrée",
    tierRemoved: "Gamme retirée",
    tierRemoveConfirm:
      "Retirer cette gamme ? Les workspaces déjà lancés continuent de tourner ; elle disparaît simplement des choix proposés.",
    tierRunningUnaffected:
      "Modifier une gamme n’a pas d’effet sur les workspaces déjà lancés : le changement s’applique aux prochains démarrages.",
    title: "Administration",
    subtitle: "Supervision et configuration de la plateforme.",
    overview: "Vue d’ensemble",
    dataUsage: "Cartographie des données",
    dataUsageHint:
      "Qui possède les datasets, qui y a accès, quels projets les consomment et quelles exécutions y sont rattachées.",
    exportCsv: "Exporter en CSV",
    audit: "Audit",
    auditHint: "Journal des actions réalisées sur la plateforme.",
    auditEmpty: "Aucun événement",
    auditComponent: "composant",
    auditPlatform: "plateforme",
    auditDetails: "Détail",
    auditEmptyHint:
      "Les actions réalisées sur la plateforme seront enregistrées ici.",
    actor: "Auteur",
    action: "Action",
    outcome: "Résultat",
    occurredAt: "Date",
    storageEndpoints: "Points de stockage",
    storageEndpointsHint:
      "Services S3 déclarés pour les datasets et les sauvegardes.",
    testEndpoint: "Tester",
    backups: "Sauvegardes",
    backupsHint:
      "Cible S3 externe utilisée pour les sauvegardes de la plateforme.",
    backupConfigure: "Configurer la cible",
    backupRun: "Lancer une sauvegarde",
    backupHistory: "Historique",
    backupEmpty: "Aucune sauvegarde",
    backupEmptyHint:
      "Configurez une cible S3 puis lancez une première sauvegarde.",
    backupIncomplete:
      "La dernière sauvegarde s’est terminée avec des réserves. Son contenu est incomplet.",
    backupNotConfigured: "Aucune cible configurée",
    backupConfigured: "Cible configurée",
    bucket: "Bucket",
    encryptionKey: "Clé de chiffrement",
    lastUpdated: "Dernière mise à jour",
  },

  graph: {
    title: "Graphe des accès",
    focusSubject: "Centrer sur une personne ou une organisation",
    focusResource: "Centrer sur une ressource",
    resourceType: "Type de ressource",
    everyone: "Tout le monde",
    everything: "Toutes les ressources",
    allTypes: "Tous les types",
    showInherited: "Afficher les accès hérités",
    hideInherited: "Masquer les accès hérités",
    clear: "Réinitialiser",
    empty: "Aucun accès ne correspond à ce filtre.",
    tooMany: "{count} accès à afficher.",
    tooManyHint:
      "Choisissez une personne, une ressource ou un type : au-delà, le graphe devient illisible et ne répond plus à rien.",
    legendDirect: "Accès direct",
    legendInherited: "Hérité d’une organisation",
    edges: "{count} accès affichés",
  },

  inventory: {
    title: "Logiciels et licences",
    subtitle:
      "Ce que la plateforme embarque, avec la licence de chaque composant. Généré depuis le graphe de dépendances réel, pas tenu à la main.",
    component: "Composant",
    version: "Version",
    licence: "Licence",
    partOf: "Fait partie de",
    source: "Origine",
    origin_detected: "Lue",
    origin_declared: "Déclarée",
    origin_unresolved: "Non résolue",
    count: "{count} composants",
    unknownCount: "{count} licence(s) non déterminée(s)",
    allKnown: "Toutes les licences sont déterminées",
    generated: "Généré le",
  },

  identity: {
    createUser: "Créer un compte",
    editUser: "Modifier",
    editUserTitle: "Corriger ce compte",
    editUserHint:
      "Corrige un nom ou un courriel mal saisi. L'identifiant ne change pas : c'est par lui que Noryx reconnaît cette personne sur chaque projet, chaque équipe et chaque entrée du journal.",
    usernameFixed: "Identifiant (non modifiable)",
    userUpdated: "Compte mis à jour",
    username: "Identifiant",
    firstName: "Prénom",
    lastName: "Nom",
    resetPassword: "Réinitialiser",
    resetPasswordHint:
      "Un nouveau mot de passe temporaire sera généré pour {user}. L’ancien cessera immédiatement de fonctionner.",
    passwordHint:
      "Affiché une seule fois et non récupérable. Transmettez-le, il devra être changé à la première connexion.",
    passwordCopied: "Mot de passe copié.",
  },

  adminNav: {
    modules: "Modules",

    people: "Personnes et accès",

    resources: "Ressources",

    operations: "Exploitation",

    compliance: "Conformité",

    configuration: "Configuration",
  },

  projectVariables: {
    title: "Variables d’environnement",

    hint: "Les réglages dont le travail a besoin, partagés par tous les membres du projet et injectés dans les workloads sous leur propre nom. Pour un identifiant personnel, utilisez un secret : il vous suit, celles-ci suivent le projet.",

    add: "Ajouter une variable",

    nameHint:
      "Lettres, chiffres et tirets bas, comme dans un shell. Les noms commençant par NORYX_ sont réservés à la plateforme.",

    valueColumn: "Valeur",

    descriptionHint: "À quoi elle sert, pour celui qui la lira dans six mois.",

    updatedColumn: "Modifiée",

    hidden: "Masquée",

    saved: "Variable enregistrée.",

    removed: "Variable supprimée.",

    emptyTitle: "Aucune variable",

    emptyHint:
      "Ajoutez l’adresse d’un serveur de suivi, un nom de bucket, une URL de registre — ce que vos workloads attendent dans leur environnement.",

    formHint:
      "La valeur est chiffrée au repos et lisible par les membres qui peuvent lancer des workloads.",

    removeTitle: "Supprimer la variable",

    removeHint: "{name} ne sera plus injectée dans les workloads de ce projet.",
  },

  account: {
    title: "Mon compte",
    subtitle:
      "Vos identifiants d’accès à l’API, vos secrets et vos préférences.",
  },

  tokens: {
    scopeLabel: "Portée",
    scopeHint:
      "Ce que le jeton pourra faire, dans la limite de vos propres droits.",
    scopeRead: "Lecture seule",
    scopeReadHint: "Consulter, ne rien modifier.",
    scopeDatasets: "Datasets",
    scopeDatasetsHint:
      "Deposer et remplacer des fichiers dans les datasets accessibles. Ne lance rien et ne supprime aucun projet.",
    scopeWorkspaces: "Workspaces",
    scopeWorkspacesHint: "Démarrer et arrêter des workspaces, plus la lecture.",
    scopeJobs: "Traitements",
    scopeJobsHint: "Lancer jobs et builds — le cas d’une intégration continue.",
    scopeFull: "Tous vos droits",
    scopeFullHint:
      "Aucune restriction : le jeton peut tout ce que vous pouvez.",
    title: "Jetons d’API",
    subtitle:
      "Pour appeler l’API hors navigateur : intégration continue, notebook, script.",
    createHint:
      "Le jeton agit en votre nom et n’a aucun droit propre : il ne peut rien faire que vous ne puissiez déjà faire.",
    nameHint: "Ce qui vous permettra de révoquer le bon plus tard.",
    namePlaceholder: "gitlab-ci",
    expiryDays: "{days} jours",
    expiryNever: "Sans expiration",
    issuedTitle: "Votre jeton",
    issuedHint:
      "Il n’est affiché qu’une fois et n’est pas récupérable. Copiez-le maintenant.",
    copied: "Jeton copié.",
    dismiss: "J’ai copié",
    empty: "Aucun jeton pour l’instant.",
    expires: "expire le",
    lastUsed: "utilisé",
    revoked: "Révoqué",
  },

  health: {
    history: "Historique",
    historyHint:
      "Conditions techniques que la plateforme a constatées elle-même. Les échecs de jobs n’y figurent pas : ils appartiennent à qui les a lancés.",
    historyEmpty: "Aucune condition enregistrée sur cette période.",
    notRecording: "L’historique n’est pas enregistré sur cette installation.",
    ongoing: "En cours",
    resolvedAfter: "Résolue après",
    raised: "Levée",
    last30: "30 jours",
    last90: "90 jours",
    last365: "1 an",
    title: "Santé de la plateforme",
    subtitle: "Conditions nécessitant une intervention.",
    checkedAt: "Vérifié",
    statusHealthy: "Tout va bien",
    statusDegraded: "Dégradée",
    statusCritical: "Critique",
    allClear: "Aucun problème détecté.",
    indicator: "Alertes plateforme",
  },
  settings: {
    key_alert_email: "Boîte mail d’alerte",
    title: "Réglages de la plateforme",
    subtitle: "Paramètres modifiables sans redéploiement.",
    precedence:
      "Un réglage défini ici prime sur la variable d’environnement, qui prime sur la valeur par défaut. Vider un champ rétablit la valeur héritée.",
    saved: "Réglage enregistré.",
    unset: "Non défini",
    key_workspace_max_lifetime: "Durée de vie maximale des workspaces",
    key_alert_webhook_url: "Webhook d’alerte",
    key_alert_instance_name: "Nom de l’instance",
    key_ui_default_theme: "Thème par défaut",
    key_platform_backend_version: "Version du backend",
    key_platform_edition: "Édition",
    key_platform_namespace: "Namespace Kubernetes",
  },
  search: {
    title: "Recherche",
    open: "Rechercher",
    placeholder: "Rechercher…",
    hint: "Saisissez au moins deux caractères.",
    empty: "Aucun résultat.",
    kind_project: "Projet",
    kind_dataset: "Dataset",
    kind_datasource: "Source",
    kind_ontology: "Ontologie",
    kind_extract: "Extrait",
    kind_repository: "Dépôt",
    kind_secret: "Secret",
    kind_workspace: "Workspace",
    kind_job: "Job",
    kind_app: "Application",
  },
  errors: {
    notFoundTitle: "Page introuvable",
    notFoundHint: "Le lien que vous avez suivi n’existe pas ou n’existe plus.",
    backHome: "Retour à l’accueil",
    crashHint:
      "L’interface a rencontré un problème. Rechargez la page ; si le problème persiste, contactez votre administrateur.",
    reload: "Recharger la page",
    projectNotFound: "Projet introuvable",
    projectNotFoundHint: "Ce projet n’existe plus ou vous n’y avez plus accès.",
    sessionExpiredTitle: "Votre session a expiré",
    sessionExpiredHint:
      "Reconnectez-vous pour reprendre. Le travail en cours dans vos espaces n’est pas affecté : ils continuent de tourner.",
    signedOutTitle: "Session expirée",
    signedOutHint: "Reconnectez-vous pour reprendre votre travail.",
    noOrganizationHint:
      "Votre compte est authentifié mais n’est rattaché à aucune organisation. Contactez un administrateur de la plateforme.",
  },
} as const;

export type Catalogue = typeof fr;
